<template>
  <div class="login_card">
    <form v-on:submit.prevent="submit" novalidate>
      <BaseInput
        v-model="email"
        :errors="emailErrors"
        :placeholder="$t('general.inputs.email')"
        :fullWidth="true"
        :height="$vuetify.breakpoint.xsOnly ? 48 : 60"
        required
        @blur="$v.email.$touch()"
        outlined
        fieldName="email"
        @change="onInputChange"
      />
      <PasswordInput
        v-model="password"
        :errors="passwordErrors"
        :placeholder="$t('general.inputs.password')"
        :height="$vuetify.breakpoint.xsOnly ? 48 : 60"
        :fullWidth="true"
        @blur="$v.password.$touch()"
        fieldName="password"
        @change="onInputChange"
      />
      <div class="login_remember-me">
        <v-checkbox v-model="rememberMe" dense :label="$t('login.rememberMe')" class="login_checkbox" />
        <span @click="passwordReset" class="login_link" href="#" v-if="!$vuetify.breakpoint.xsOnly">{{
          $t("login.resetPasswordLink")
        }}</span>
      </div>
      <PrimaryButton :text="$t('login.button')" type="submit" size="x-large" />

      <span @click="passwordReset" class="login_link mt-8" v-if="$vuetify.breakpoint.xsOnly">{{
        $t("login.resetPasswordLink")
      }}</span>
    </form>
    <Snackbar v-model="snackbar" type="error" :text="error" :timeout="4000" @toggleSnackbar="toggleSnackbar" />
  </div>
</template>

<script>
import { email, required } from "vuelidate/lib/validators";
import { mapActions, mapGetters } from "vuex";

import PasswordInput from "@/components/shared/PasswordInput";
import Snackbar from "@/components/shared/Snackbar";
import BaseInput from "@/components/uikit/BaseInput";
import PrimaryButton from "@/components/uikit/PrimaryButton";
import { RolesEnum } from "@/types/Roles.enum";

export default {
  name: "Login",
  components: {
    BaseInput,
    PasswordInput,
    PrimaryButton,
    Snackbar,
  },
  props: ["renderRole"],
  validations: {
    email: { required, email },
    password: { required },
  },
  data: () => ({
    email: "",
    password: "",
    rememberMe: true,
    showPassword: false,
    snackbar: false,
    error: "",
    loading: false,
  }),
  computed: {
    ...mapGetters(["role"]),
    passwordErrors() {
      const errors = [];
      if (!this.$v.password.$dirty) return errors;
      !this.$v.password.required &&
        errors.push(this.$t("validation.required", { field: this.$t("general.inputs.password") }));
      return errors;
    },
    emailErrors() {
      const errors = [];
      if (!this.$v.email.$dirty) return errors;
      !this.$v.email.email && errors.push(this.$t("validation.email"));
      !this.$v.email.required &&
        errors.push(this.$t("validation.required", { field: this.$t("general.inputs.email") }));
      return errors;
    },
  },

  methods: {
    ...mapActions(["submitLogin", "resetPassword"]),
    async passwordReset() {
      await this.$router.push({ name: "requestResetPassword" });
    },
    toggleSnackbar() {
      this.snackbar = !this.snackbar;
    },
    onInputChange(value, fieldName) {
      this[fieldName] = value;
    },
    async submit() {
      this.$gtag.event("login", { method: "Login" });
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }
      try {
        this.loading = true;
        await this.submitLogin({
          email: this.email,
          password: this.password,
          rememberMe: this.rememberMe,
        });
        const redirect = this.$route.query.redirect;
        if (redirect) {
          await this.$router.push({ path: redirect });
        } else if (this.role === RolesEnum.Patient) {
          await this.$router.push({ path: "/patient/visits/upcoming" });
        } else {
          await this.$router.push({ path: "/practitioner/appointments" });
        }
      } catch (err) {
        this.snackbar = true;
        this.error = err.message;
      }
      this.loading = false;
    },
  },
};
</script>

<style lang="scss">
.login {
  &_card {
    text-align: center;
    width: 440px;
    margin: 10px auto;

    @media (max-width: 767.98px) {
      width: auto;
      margin: 10px 20px;
    }
  }

  &_remember-me {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &_checkbox {
    cursor: pointer;

    label {
      font-style: normal;
      font-weight: normal;
      font-size: 13px;
      line-height: 16px;
      color: $primaryblack;

      opacity: 0.36;
    }
  }

  &_link {
    cursor: pointer;
    display: inline-block;
    font-style: normal;
    font-weight: 500;
    font-size: 13px;
    line-height: 16px;
    text-align: right;

    color: $primaryblack;

    opacity: 0.4;
  }
}
</style>
